import {
  CreateUserWithEmailLinkResponse,
  EncryptedUser,
  Language,
  MalpracticeInsurance,
  Office,
  OfficeEmailInvitation,
  Role,
  UserStatus,
} from '@caresend/types';
import {
  dbGroupSet,
  dbSet,
  firebaseAuth,
  getStore,
  toastError,
  toastResolve,
  toastSuccess,
} from '@caresend/ui-components';
import { getUserPath } from '@caresend/utils';
import { signInWithCustomToken } from 'firebase/auth';
import { Route } from 'vue-router';

import { patientURL, webProURL } from '@/data/urls';
import {
  changeUserEmailAddressRequest,
  checkEmailAlreadyInUseRequest,
  createUserWithEmailLinkRequest,
  refreshTokenOnFailureRequest,
  resetPasswordRequest,
  sendFinishSignUpEmailRequest,
} from '@/database/firebase/API';
import { identifyAnonymousUserSegment } from '@/functions/tracking';
import { trackDebugEvent } from '@/functions/tracking/tracking';

export const registerUserWithoutPassword = (
  user: EncryptedUser,
  members?: Array<EncryptedUser>,
  office?: Office,
  invitations?: OfficeEmailInvitation[],
): Promise<CreateUserWithEmailLinkResponse> =>
  new Promise<CreateUserWithEmailLinkResponse>((resolve, reject) => {
    const emailLinkURL = user.role === 'patient' ? patientURL : webProURL;
    createUserWithEmailLinkRequest({ user, emailLinkURL, members, office, invitations })
      .then((result: any) => toastResolve(resolve, 'Confirmation email sent!', result))
      .catch((error: any) => reject(toastError(error.message)));
  });

export const sendFinishSignUpLink = (email: string): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    sendFinishSignUpEmailRequest({ email, emailLinkURL: webProURL })
      .then(() => toastResolve(resolve, 'Confirmation email sent!', undefined))
      .catch((error) => reject(toastError(error.message)));
  });

export const changeUserEmailAddress = (email: string, newEmail: string): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    changeUserEmailAddressRequest({ email, newEmail })
      .then(() => toastResolve(resolve, 'Your email has been changed', undefined))
      .catch((error) => reject(toastError(error.message)));
  });

export const signInWithToken = async (
  token: string,
  to: Route,
  isSecondTry?: boolean,
): Promise<void> => {
  try {
    await signInWithCustomToken(firebaseAuth, token);
  } catch (error) {
    if (isSecondTry) {
      throw toastError('Something went wrong. Contact support to access your account.');
    } else {
      console.info(
        'Initial authentication failed, trying token refresh. Initial error:',
        error,
      );
    }

    const route = to.fullPath.replace('/', '');
    const response = await refreshTokenOnFailureRequest({
      emailLinkURL: webProURL,
      route,
      token,
    });

    if (!response.token) {
      throw toastError('This token has expired. Check your email for a new link.');
    }

    const newToken = response.token;
    return signInWithToken(newToken, to, true);
  }
};

export const verifyStatusIncomplete = async (): Promise<true> => {
  const store = getStore();

  const { user } = store.state.auth;
  if (!user) {
    throw new Error('User not authenticated when verifying status.');
  }

  const { passwordCreated, status } = user;
  if (status === UserStatus.INCOMPLETE || !passwordCreated) return true;
  throw toastError('Account already registered');
};

export const resetPassword = async (email: string): Promise<boolean> => {
  const isEmailSent = await resetPasswordRequest({ email, emailLinkURL: webProURL });
  if (!isEmailSent) throw toastError('Something went wrong. Contact support to reset your password.');

  toastSuccess('We just sent you an email, please check your inbox to reset your password.');
  return isEmailSent;
};

/** Firebase auth has initialized and the user is not signed in. */
export const handleAuthInitializedWithoutUser = () => identifyAnonymousUserSegment();

export const checkEmailAlreadyInUse = async (
  email: string,
): Promise<{ email: string; isInUse: boolean }> => {
  const { isInUse } = await checkEmailAlreadyInUseRequest({ email });
  return { isInUse, email };
};

export const updateUserStatusOnVerification = async (
  user: EncryptedUser,
  /** Temporary - remove this once we solidify the password creation process */
  debugData: any,
): Promise<void> => {
  const { role, offices } = user;
  let newStatus: UserStatus | undefined;
  switch (role) {
    case Role.NURSE:
      if (offices && offices.length > 0) {
        newStatus = UserStatus.APPROVED;
      } else {
        newStatus = UserStatus.PENDING;
      }
      break;
    case Role.PATIENT:
    case Role.PRESCRIBER:
    case Role.ASSISTANT:
      newStatus = UserStatus.APPROVED;
      break;
  }
  const updatedDebugData = {
    ...debugData,
    newStatus,
  };
  await trackDebugEvent('newStatus check', updatedDebugData);
  if (!newStatus) throw new Error('Failed to verify user status');

  await trackDebugEvent('updating user status', updatedDebugData);
  await dbSet<UserStatus>(getUserPath(user.id, 'status'), newStatus);
};

export const completeNurseInfo = async (
  userID: string,
  languages: Language[],
  malpracticeInsurance?: MalpracticeInsurance,
): Promise<void> => {
  const userPath = getUserPath(userID);
  type UpdateType = MalpracticeInsurance | Language[] | UserStatus;
  const update: Record<string, UpdateType> = {
    [`${userPath}/info/languages`]: languages,
    [`${userPath}/status`]: UserStatus.PENDING,
  };
  if (malpracticeInsurance) {
    update[`${userPath}/professionalInfo/malpracticeInsurance`] = malpracticeInsurance;
  }
  await dbGroupSet<UpdateType>(update);
};
